// @flow
import React, { Component } from 'react';
import styled from 'styled-components';
import { themes, utils } from 'kb-shared';
import { Query, Mutation, QueryResult } from 'react-apollo';
import gql from 'graphql-tag';
import StepTracker from '../../components/StepTracker';
import MobileStepTracker from '../../components/MobileStepTracker';
import AuthFlowLayout from '../../components/AuthFlowLayout';
import { TitleText, AllSetContainer, FinishedSubtitle, Link } from './Steps/Comprehension';
import Button from '../../components/Button/NewButton';

// Steps
import YourConsent from './Steps/YourConsent';
import AdditionalOptions from './Steps/AdditionalOptions';
import Comprehension from './Steps/Comprehension';
import ConsentStateManager, { StepType, Data } from './ConsentStateManager';
import { withRouter, RouteComponentProps } from 'react-router';

export type ConsentOption = {
  consentDecisionId: string;
  description: string;
  id: string;
  name: string;
};

export type ConsentDecision = {
  id: string;
  name: string;
  consentId: number;
  description: string;
  consentOptions: ConsentOption[];
};

export type ComprehensionQuestion = {
  consentId: string;
  id: string;
  correctResponse: string;
  explanation: string;
  options: string[];
  text: string;
};

interface Consent {
  consent: {
    htmlLink: string;
    id: string;
    name: string;
    comprehensionQuestions: ComprehensionQuestion[];
    consentDecisions: ConsentDecision[];
  };
}

export type ConsentData = QueryResult<Consent>;

const OPEN_PATIENT_CONSENT = gql`
  query openPatientConsent($consentId: Int!) {
    openPatientConsent(consentId: $consentId) {
      id
    }
  }
`;

const LOAD_CONSENTS = gql`
  query consent($consentId: Int!) {
    consent(id: $consentId) {
      htmlLink
      id
      name
      comprehensionQuestions {
        consentId
        id
        correctResponse
        explanation
        options
        text
      }
      consentDecisions {
        id
        name
        consentId
        description
        consentOptions {
          consentDecisionId
          description
          id
          name
        }
      }
    }
  }
`;

interface CompleteFormResponseData {
  id: number;
  consentId: string;
  consentedAt: string;
}

const COMPLETE_FORM = gql`
  mutation completePatientConsent($patientConsentId: Int!) {
    completePatientConsent(patientConsentId: $patientConsentId) {
      patientConsent {
        id
        consentId
        consentedAt
      }
    }
  }
`;

const Container = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const MobileStepTrackerWrapper = styled.div`
  padding-top: 24px;
  padding-bottom: 24px;
`;

const RightContentWrapper = styled.div`
  padding-right: 24px;
  position: fixed;
`;

const PageTitle = styled.div`
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  ${themes.fonts.DomaineDisplay};
  color: ${themes.colors.KBNight};
  font-size: 36px;
  @media screen and (min-width: ${themes.breakpoints.smallDesktop}) {
    font-size: 48px;
  }
`;

type Props = RouteComponentProps & {};

type State = {
  data: Data;
  formName: string;
  consentId: number;

  comprehensionHasNoQuestions?: boolean;
};

class InformedConsent extends Component<Props, State> {
  dataManager: ConsentStateManager;

  constructor(props: Props) {
    super(props);
    // @ts-ignore
    const { id } = props.computedMatch.params;
    this.dataManager = new ConsentStateManager(this.updateData);
    this.state = {
      data: this.dataManager.data,
      formName: '',
      consentId: parseInt(id)
    };
  }

  componentDidMount() {
    this.setState({
      data: this.dataManager.data
    });
  }

  updateData = (data: Data) => {
    this.setState({
      data: data
    });
  };

  getPageTitle(step: StepType) {
    const { comprehensionHasNoQuestions } = this.state;
    switch (step) {
      case 'your_consent':
        return this.state.formName;
      case 'additional_options':
        return 'Let’s go over a few more things:';
      case 'comprehension':
        return comprehensionHasNoQuestions ? '' : 'Let’s go over a few more things:';
      default:
        return '';
    }
  }

  getStepsNames(): { stepNames: string[]; currentStep: number; pageTitle: string } {
    const {
      data: { steps, selectedStep }
    } = this.state;
    let currentStep = 0;
    let pageTitle = '';
    const stepNames = Object.keys(steps)
      // @ts-ignore
      .map((key: StepType, index: number) => {
        const step = steps[key];
        if (step.type === selectedStep) {
          currentStep = index;
          pageTitle = this.getPageTitle(step.type);
        }
        return step.name;
      })
      .filter((name: string) => name); // skip blank/hidden steps
    return {
      stepNames,
      currentStep,
      pageTitle
    };
  }

  renderCurrentStep() {
    return (
      <Query
        query={LOAD_CONSENTS}
        variables={{ consentId: this.state.consentId }}
        onCompleted={(data: Consent) => {
          this.setState({ formName: data.consent.name });
          if (data.consent.consentDecisions.length < 1) {
            this.dataManager.setNoAdditionalOptions();
          }

          if (data.consent.comprehensionQuestions.length == 0) {
            this.dataManager.setNoComprehensionQuestions();
          }
        }}
      >
        {({ data, loading }: ConsentData) => {
          const { selectedStep } = this.state.data;
          if (loading || !data || !data.consent) {
            return null;
          }
          const consent = data.consent;
          switch (selectedStep) {
            case 'your_consent':
              const htmlLink = consent.htmlLink;
              return (
                <>
                  <YourConsent
                    consentFormId={this.state.consentId}
                    consentLinkHTML={htmlLink}
                    onAgree={(patientConsentId: string) => {
                      this.dataManager.setUserDidAgreeToConsent(patientConsentId);
                    }}
                  />
                </>
              );
            case 'additional_options':
              return (
                <>
                  <AdditionalOptions
                    patientConsentId={this.state.data.patientConsentId}
                    consentDecisions={consent.consentDecisions}
                    onCompleted={() => {
                      this.dataManager.setUserDidCompleteConsentDecisions();
                    }}
                  />
                </>
              );
            case 'comprehension':
              const questions = data.consent.comprehensionQuestions;

              const { comprehensionHasNoQuestions } = this.state;
              if (!comprehensionHasNoQuestions && questions.length == 0) {
                this.setState({
                  comprehensionHasNoQuestions: true
                });
              }

              return (
                <Mutation<CompleteFormResponseData, { patientConsentId: number }>
                  mutation={COMPLETE_FORM}
                >
                  {completeForm => {
                    return (
                      <Comprehension
                        patientConsentId={this.state.data.patientConsentId}
                        questions={questions}
                        onCompleted={() => {
                          completeForm({
                            variables: {
                              patientConsentId: parseInt(this.state.data.patientConsentId)
                            }
                          });
                        }}
                        onBackToDashboard={() => {
                          this.props.history.push('/');
                        }}
                      />
                    );
                  }}
                </Mutation>
              );
            default:
              return null;
          }
        }}
      </Query>
    );
  }

  render() {
    const { stepNames, currentStep, pageTitle } = this.getStepsNames();
    return (
      <Query query={OPEN_PATIENT_CONSENT} variables={{ consentId: this.state.consentId }}>
        {({ data, loading }: ConsentData) => {
          if (loading) {
            return null;
          }
          // @ts-ignore
          else if (!data || !data.openPatientConsent) {
            // do not show consent at all if they do not have an outstanding consent to complete
            return (
              <AllSetContainer>
                <TitleText>You&apos;re all set!</TitleText>
                <FinishedSubtitle>
                  If you have any questions, contact your care team at{' '}
                  <Link href={`mailto:${utils.KBContacts.clinical}?subject=Consent Forms`}>
                    {utils.KBContacts.clinical}
                  </Link>{' '}
                  or <span style={{ fontWeight: 'bold' }}>1-855-KND-BODY</span>
                </FinishedSubtitle>
                <Button
                  text={'Back to dashboard'}
                  onClick={() => {
                    this.props.history.push('/');
                  }}
                />
              </AllSetContainer>
            );
          }
          return (
            <AuthFlowLayout
              title={''}
              renderAboveTitleContent={(mobile: boolean) => {
                if (mobile && this.state.formName) {
                  return (
                    <>
                      <PageTitle>{pageTitle}</PageTitle>
                      <MobileStepTrackerWrapper>
                        <MobileStepTracker steps={stepNames} selectedStep={currentStep} />
                      </MobileStepTrackerWrapper>
                    </>
                  );
                } else {
                  return <PageTitle>{pageTitle}</PageTitle>;
                }
              }}
              renderMainContent={() => {
                return (
                  <Container>
                    <Content>{this.renderCurrentStep()}</Content>
                  </Container>
                );
              }}
              renderDesktopRightSidebar={() => {
                if (!this.state.formName) {
                  return null;
                }
                const selectedStep = Math.min(currentStep, stepNames.length - 1); // prevent hidden steps from showing as selected
                return (
                  <RightContentWrapper>
                    <StepTracker
                      steps={stepNames}
                      selectedStep={selectedStep}
                      onSelectStep={(index: number) => {
                        const step = this.dataManager.stepAtIndex(index);
                        this.dataManager.goBackToStep(step.type);
                      }}
                    />
                  </RightContentWrapper>
                );
              }}
            />
          );
        }}
      </Query>
    );
  }
}

export default withRouter(InformedConsent);
